import { useMemo } from 'react'
import { Image } from 'src/components/ui/Image'
import { PictureCountIcon } from 'src/components/Icons/PictureCountIcon'

import type { Image as ProductImage } from '../../../types/productItem'
import { useMiniPdpImageCarousel } from './useMiniPdpImageCarousel'

type MiniPdpImageCarouselProps = {
  images: ProductImage[]
  productName: string
}

export function MiniPdpImageCarousel({
  images,
  productName,
}: MiniPdpImageCarouselProps) {
  const normalizedImages = useMemo(
    () => images.filter((imageItem) => Boolean(imageItem?.imageUrl)),
    [images]
  )

  const {
    viewportRef,
    trackRef,
    currentIndex,
    trackStyle,
    handlePointerDown,
    handlePointerMove,
    endDrag,
  } = useMiniPdpImageCarousel(normalizedImages.length)

  if (normalizedImages.length === 0) {
    return null
  }

  return (
    <section className="-mx-4 mb-6 restructure-tablet:-mx-8">
      <div className="relative">
        {normalizedImages.length > 1 && (
          <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-round border border-restructure-border-disabled bg-white px-3 py-1.5 text-font-size-1 text-restructure-primary shadow-sm">
            <PictureCountIcon />
            {currentIndex + 1}/{normalizedImages.length}
          </div>
        )}

        <div
          ref={viewportRef}
          className="cursor-grab touch-pan-y overflow-hidden select-none active:cursor-grabbing"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            ref={trackRef}
            data-testid="mini-pdp-image-track"
            className="flex w-full gap-[10px] restructure-tablet:pr-8"
            style={trackStyle}
          >
            {normalizedImages.map((imageItem, index) => (
              <div
                key={`${imageItem.imageUrl}-${index}`}
                className="max-w-[448px] w-[60%] shrink-0 restructure-tablet:w-full bg-restructure-background-neutral-3"
              >
                <Image
                  src={imageItem.imageUrl}
                  alt={productName}
                  title={imageItem.imageText ?? productName}
                  width={448}
                  height={322}
                  loading="lazy"
                  sizes="100vw"
                  draggable={false}
                  className="max-w-[448px] h-[260px] w-full object-contain mix-blend-multiply restructure-tablet:h-[360px]"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
